import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { LoggerService } from '../logger/logger.service';
import { ROUTES } from '../routes';
import { AnalyticsService } from './analytics.service';
import { AnalyticsEvents, AnalyticsPages } from './analytics.constants';
import { isInstalledAppMode } from './device-context';
import {
  StartupMilestones,
  hasStartupMilestone,
  markStartup,
  noteStartupContext,
  startupContext,
  startupTimings,
  type StartupMilestone,
} from './startup-timing';

const TAG = 'StartupTiming';

/**
 * Reports one cold start: the milestones in startup-timing.ts plus what explains them (installed
 * app, service-worker-served, whether a silent refresh ran, first screen). Phase A of
 * docs/cold-start-optimization-plan.md — the before/after numbers for every later change.
 *
 * The report goes out once, when the start is over:
 * - first screen is the Dashboard → when it has its data (`dashboard_ready`), or after
 *   {@link DASHBOARD_READY_MAX_WAIT_MS} with whatever was reached, so a hung API still reports;
 * - any other first screen (Login, onboarding) → as soon as it is activated.
 * A start that never reaches a route (maintenance, unsupported device/browser, install gate)
 * reports nothing — those screens are not the cold start being measured.
 *
 * Always logged to the console (non-production only, LoggerService). Sent as the `cold_start`
 * functional log only where `environment.startupTimingAnalytics` is on: the event is new to the
 * backend, so production stays off until it is accepted there. Pre-login it rides the guest
 * token, which the backend only accepts for the allowlisted guest pages — the post-login
 * (Dashboard) starts are the ones that matter for auto-start anyway.
 */
@Injectable({ providedIn: 'root' })
export class StartupTimingService {
  static readonly DASHBOARD_READY_MAX_WAIT_MS = 60_000;

  private readonly analytics = inject(AnalyticsService);
  private readonly logger = inject(LoggerService);

  private reported = false;
  private waitingForDashboard = false;
  private dashboardTimer: ReturnType<typeof setTimeout> | null = null;

  mark(milestone: StartupMilestone): void {
    markStartup(milestone);
    if (milestone === StartupMilestones.DASHBOARD_READY && this.waitingForDashboard) {
      this.report();
    }
  }

  /** Called on the first finished navigation; later ones are ignored. */
  firstRouteActivated(url: string): void {
    if (hasStartupMilestone(StartupMilestones.FIRST_ROUTE)) return;
    markStartup(StartupMilestones.FIRST_ROUTE);
    const path = url.split(/[?#]/)[0] || '/';
    noteStartupContext({ first_route: path });

    if (path !== `/${ROUTES.DASHBOARD}` || hasStartupMilestone(StartupMilestones.DASHBOARD_READY)) {
      this.report();
      return;
    }
    this.waitingForDashboard = true;
    this.dashboardTimer = setTimeout(
      () => this.report(),
      StartupTimingService.DASHBOARD_READY_MAX_WAIT_MS,
    );
  }

  private report(): void {
    if (this.reported) return;
    this.reported = true;
    this.waitingForDashboard = false;
    if (this.dashboardTimer !== null) {
      clearTimeout(this.dashboardTimer);
      this.dashboardTimer = null;
    }

    const fields = {
      startup_timings: startupTimings(),
      ...startupContext(),
      installed_app: isInstalledAppMode(),
      sw_controlled: serviceWorkerControlled(),
    };
    this.logger.info(TAG, 'cold start', fields);
    if (environment.startupTimingAnalytics?.enabled) {
      this.analytics.log(AnalyticsEvents.COLD_START, AnalyticsPages.APP_STARTUP, fields);
    }
  }
}

/** Whether this page load was served by the service worker (false on a first-ever visit). */
function serviceWorkerControlled(): boolean {
  try {
    return typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;
  } catch {
    return false;
  }
}
